// Mercapoli - Tracking de Pedido en Tiempo Real
import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import { motion } from 'framer-motion';
import { MapPin, Bike, Store, Clock, Phone, Navigation, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { tracking as trackingApi } from '../lib/api';
import 'leaflet/dist/leaflet.css';

import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Custom icons
const riderIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const restaurantIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const destinoIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Auto-fit bounds component
const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = positions.map(p => [p.lat, p.lng]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [positions, map]);
  return null;
};

const OrderTracking = ({ pedidoId, height = "400px" }) => {
  const [loading, setLoading] = useState(true);
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);

  const loadTracking = useCallback(async () => {
    try {
      const data = await trackingApi.getTrackingPedido(pedidoId);
      setTrackingData(data);
      setError(null);
    } catch (err) {
      setError('Error cargando tracking');
      console.error(err);
    }
    setLoading(false);
  }, [pedidoId]);

  useEffect(() => {
    loadTracking();
    // Actualizar cada 10 segundos cuando está en camino
    const interval = setInterval(() => {
      if (trackingData?.estado === 'en_camino') {
        loadTracking();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [loadTracking, trackingData?.estado]);

  const getEstadoConfig = (estado) => {
    const configs = {
      pendiente: { color: 'bg-yellow-500', label: 'Pendiente', icon: Clock },
      aceptado: { color: 'bg-blue-500', label: 'Aceptado', icon: Store },
      preparando: { color: 'bg-purple-500', label: 'Preparando', icon: Store },
      listo: { color: 'bg-green-500', label: 'Listo', icon: Store },
      en_camino: { color: 'bg-cyan-500', label: 'En camino', icon: Bike },
      entregado: { color: 'bg-emerald-500', label: 'Entregado', icon: MapPin },
      cancelado: { color: 'bg-red-500', label: 'Cancelado', icon: MapPin },
    };
    return configs[estado] || configs.pendiente;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (error || !trackingData) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <MapPin className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">{error || 'No se encontró el pedido'}</p>
        </CardContent>
      </Card>
    );
  }

  const estadoConfig = getEstadoConfig(trackingData.estado);
  const EstadoIcon = estadoConfig.icon;

  // Preparar posiciones para el mapa
  const positions = [];
  if (trackingData.restaurante?.latitud) {
    positions.push({ lat: trackingData.restaurante.latitud, lng: trackingData.restaurante.longitud, type: 'restaurante' });
  }
  if (trackingData.rider_ubicacion?.latitud) {
    positions.push({ lat: trackingData.rider_ubicacion.latitud, lng: trackingData.rider_ubicacion.longitud, type: 'rider' });
  }
  if (trackingData.destino?.latitud) {
    positions.push({ lat: trackingData.destino.latitud, lng: trackingData.destino.longitud, type: 'destino' });
  }

  // Default center
  const defaultCenter = positions.length > 0 
    ? [positions[0].lat, positions[0].lng]
    : [14.6349, -90.5069];

  // Línea de ruta
  const routeLine = positions.length >= 2 
    ? positions.map(p => [p.lat, p.lng])
    : [];

  return (
    <div className="space-y-4">
      {/* Estado del pedido */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full ${estadoConfig.color} flex items-center justify-center`}>
                <EstadoIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-lg">{estadoConfig.label}</p>
                <p className="text-sm text-muted-foreground">Pedido #{pedidoId.slice(-6)}</p>
              </div>
            </div>
            
            {trackingData.tiempo_estimado_min && trackingData.estado === 'en_camino' && (
              <div className="text-right">
                <p className="text-2xl font-bold text-brand-primary">{trackingData.tiempo_estimado_min} min</p>
                <p className="text-xs text-muted-foreground">tiempo estimado</p>
              </div>
            )}
          </div>

          {/* Info del rider */}
          {trackingData.rider && trackingData.estado === 'en_camino' && (
            <div className="mt-4 p-3 bg-muted rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                  <Bike className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="font-medium">{trackingData.rider.nombre}</p>
                  <p className="text-xs text-muted-foreground">Tu repartidor</p>
                </div>
              </div>
              {trackingData.rider.telefono && (
                <a href={`tel:${trackingData.rider.telefono}`}>
                  <Button variant="outline" size="sm">
                    <Phone className="h-4 w-4 mr-1" />
                    Llamar
                  </Button>
                </a>
              )}
            </div>
          )}

          {/* Distancia restante */}
          {trackingData.distancia_restante_km && trackingData.estado === 'en_camino' && (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Navigation className="h-4 w-4" />
              <span>A {trackingData.distancia_restante_km} km de tu ubicación</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mapa */}
      <div className="rounded-xl overflow-hidden border border-border" style={{ height }}>
        <MapContainer
          center={defaultCenter}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {positions.length > 0 && <FitBounds positions={positions} />}

          {/* Restaurante */}
          {trackingData.restaurante?.latitud && (
            <Marker 
              position={[trackingData.restaurante.latitud, trackingData.restaurante.longitud]} 
              icon={restaurantIcon}
            >
              <Popup>
                <div className="text-center">
                  <Store className="h-5 w-5 mx-auto mb-1 text-green-600" />
                  <strong>{trackingData.restaurante.nombre}</strong>
                  <p className="text-xs">{trackingData.restaurante.direccion}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Rider (solo si está en camino) */}
          {trackingData.rider_ubicacion?.latitud && trackingData.estado === 'en_camino' && (
            <Marker 
              position={[trackingData.rider_ubicacion.latitud, trackingData.rider_ubicacion.longitud]} 
              icon={riderIcon}
            >
              <Popup>
                <div className="text-center">
                  <Bike className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                  <strong>{trackingData.rider?.nombre || 'Repartidor'}</strong>
                  <p className="text-xs">En camino</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Destino */}
          {trackingData.destino?.latitud && (
            <Marker 
              position={[trackingData.destino.latitud, trackingData.destino.longitud]} 
              icon={destinoIcon}
            >
              <Popup>
                <div className="text-center">
                  <MapPin className="h-5 w-5 mx-auto mb-1 text-red-600" />
                  <strong>Tu ubicación</strong>
                  <p className="text-xs">{trackingData.direccion_entrega}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Línea de ruta */}
          {routeLine.length >= 2 && (
            <Polyline 
              positions={routeLine} 
              color="#00BFA6" 
              weight={4}
              dashArray="10, 10"
            />
          )}
        </MapContainer>
      </div>

      {/* Refresh button */}
      <div className="flex justify-center">
        <Button variant="outline" size="sm" onClick={loadTracking}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar ubicación
        </Button>
      </div>
    </div>
  );
};

export default OrderTracking;
